import {useContext} from 'react'

import PropTypes from 'prop-types'

import Context from '@s-ui/react-context'
import {Badge, BADGE_DESIGNS, BADGE_INTENTS, BADGE_SIZES} from '@adv-mt/ui/badge'

const MONTHS_IN_YEAR = 12

const getTaxBadges = ({
  hasOptionalEquipment,
  i18n,
  includesTaxes,
  isBusinessTargetBuyer,
  isProfessional,
  isRentingAd,
  taxTypeId,
  taxTypes
}) => {
  // Renting always reads IVA and derives the tax state from the target buyer, never from includesTaxes
  const taxTypeLabel = isRentingAd ? i18n.t('COMMON_VAT') : taxTypes.find(({id}) => id === taxTypeId)?.label
  const showTaxes = Boolean(taxTypeLabel) && (isRentingAd || isProfessional)
  const areTaxesIncluded = isRentingAd ? !isBusinessTargetBuyer : includesTaxes

  const extrasBadge = {key: 'extras', label: i18n.t('COMMON.DETAIL_EXTRAS_INCLUDED')}

  if (!showTaxes) return hasOptionalEquipment ? [extrasBadge] : []

  if (areTaxesIncluded) {
    return hasOptionalEquipment
      ? [{key: 'taxes', label: i18n.t('COMMON.DETAIL_TAXES_AND_EXTRAS_INCLUDED', {taxType: taxTypeLabel})}]
      : [{key: 'taxes', label: `${taxTypeLabel} ${i18n.t('CARD_AD_INCLUDED')}`}]
  }

  const taxesBadge = {key: 'taxes', label: `${taxTypeLabel} ${i18n.t('CARD_AD_NOT_INCLUDED')}`}

  return hasOptionalEquipment ? [taxesBadge, extrasBadge] : [taxesBadge]
}

const getWarrantyLabel = ({i18n, showWarrantyFreeText, warrantyMonths, warrantyText}) => {
  if (warrantyMonths) {
    const period =
      warrantyMonths % MONTHS_IN_YEAR === 0
        ? i18n.t('COMMON_N_YEARS', warrantyMonths / MONTHS_IN_YEAR)
        : i18n.t('COMMON_N_MONTHS', warrantyMonths)

    return i18n.t('COMMON.DETAIL_WARRANTY_PERIOD', {period})
  }

  return showWarrantyFreeText || warrantyText ? i18n.t('CARD_AD_WARRANTY_FREE_TEXT') : null
}

const Badges = ({ad = {}, className, isBusinessTargetBuyer, isRentingAd, optionalEquipment = []}) => {
  const {i18n, domain} = useContext(Context)
  const config = domain.get('config')

  const {SHOW_WARRANTY_FREE_TEXT, TAGS_VISIBILITY, taxTypes} = config.get('search')

  const {hasWarranty, includesTaxes, isProfessional, location, taxTypeId, warrantyMonths, warrantyText} = ad

  const warrantyLabel = hasWarranty
    ? getWarrantyLabel({i18n, showWarrantyFreeText: SHOW_WARRANTY_FREE_TEXT, warrantyMonths, warrantyText})
    : null
  const hasFlexibleDelivery = TAGS_VISIBILITY.MULTI_PROVINCE && location?.provinceIds?.length > 1

  const badges = [
    ...getTaxBadges({
      hasOptionalEquipment: optionalEquipment.length > 0,
      i18n,
      includesTaxes,
      isBusinessTargetBuyer,
      isProfessional,
      isRentingAd,
      taxTypeId,
      taxTypes
    }),
    warrantyLabel && {key: 'warranty', label: warrantyLabel},
    hasFlexibleDelivery && {key: 'delivery', label: i18n.t('COMMON.FLEXIBLE_DELIVERY')}
  ].filter(Boolean)

  if (badges.length === 0) return null

  return (
    <div className={className}>
      {badges.map(({key, label}) => (
        <Badge key={key} size={BADGE_SIZES.MEDIUM} intent={BADGE_INTENTS.NEUTRAL} design={BADGE_DESIGNS.SOFT}>
          {label}
        </Badge>
      ))}
    </div>
  )
}

Badges.displayName = 'Badges'
Badges.propTypes = {
  ad: PropTypes.object,
  className: PropTypes.string,
  isBusinessTargetBuyer: PropTypes.bool,
  isRentingAd: PropTypes.bool,
  optionalEquipment: PropTypes.array
}

export default Badges
